/**
 * Books cutover / opening balances for mid-year migration.
 * Local-first; optionally mirrored to the open ERP financial year.
 */
import { useEffect, useState } from 'react';
import { loadDevJson, saveDevJson } from './devStorage';
import { defaultAdminEntryDate, resolveWorkspaceAsOfDate } from './workspaceAsOf';

export const BOOKS_OPENING_KEY = 'books_opening_v1';
export const BOOKS_OPENING_EVENT = 'books-opening-data';

export type BooksOpeningBalances = {
  cash: number;
  bank: number;
  /** Loans receivable / portfolio outstanding (control total) */
  outstanding: number;
  capital: number;
  loansTaken: number;
  creditors: number;
  inventory: number;
  employeeAdvances: number;
  /** Optional YTD P&L seeds for mid-year go-live */
  ytdInterestIncome: number;
  ytdExpenses: number;
};

export type BooksOpeningState = {
  cutoverDate: string;
  balances: BooksOpeningBalances;
  notes: string;
  updatedAt: string;
};

function emptyBalances(): BooksOpeningBalances {
  return {
    cash: 0,
    bank: 0,
    outstanding: 0,
    capital: 0,
    loansTaken: 0,
    creditors: 0,
    inventory: 0,
    employeeAdvances: 0,
    ytdInterestIncome: 0,
    ytdExpenses: 0,
  };
}

export function emptyBooksOpening(): BooksOpeningState {
  return {
    cutoverDate: defaultAdminEntryDate() || resolveWorkspaceAsOfDate() || '',
    balances: emptyBalances(),
    notes: '',
    updatedAt: '',
  };
}

function emit() {
  try {
    window.dispatchEvent(new Event(BOOKS_OPENING_EVENT));
    window.dispatchEvent(new Event('workspace-data'));
  } catch {
    /* ignore */
  }
}

export function readBooksOpening(): BooksOpeningState {
  const raw = loadDevJson<Partial<BooksOpeningState> | null>(BOOKS_OPENING_KEY, null);
  if (!raw) return emptyBooksOpening();
  return {
    cutoverDate: String(raw.cutoverDate || emptyBooksOpening().cutoverDate),
    balances: { ...emptyBalances(), ...(raw.balances || {}) },
    notes: String(raw.notes || ''),
    updatedAt: String(raw.updatedAt || ''),
  };
}

export function writeBooksOpening(next: BooksOpeningState): BooksOpeningState {
  const saved: BooksOpeningState = {
    ...next,
    balances: { ...emptyBalances(), ...next.balances },
    updatedAt: new Date().toISOString(),
  };
  saveDevJson(BOOKS_OPENING_KEY, saved);
  emit();
  return saved;
}

export function useBooksOpening(): [BooksOpeningState, (patch: Partial<BooksOpeningState> | ((prev: BooksOpeningState) => BooksOpeningState)) => void] {
  const [state, setState] = useState(readBooksOpening);
  useEffect(() => {
    const sync = () => setState(readBooksOpening());
    window.addEventListener(BOOKS_OPENING_EVENT, sync);
    window.addEventListener('workspace-data', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(BOOKS_OPENING_EVENT, sync);
      window.removeEventListener('workspace-data', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);
  const update = (patch: Partial<BooksOpeningState> | ((prev: BooksOpeningState) => BooksOpeningState)) => {
    const prev = readBooksOpening();
    const next = typeof patch === 'function' ? patch(prev) : { ...prev, ...patch, balances: { ...prev.balances, ...(patch.balances || {}) } };
    setState(writeBooksOpening(next));
  };
  return [state, update];
}

/** ERP / UI field keys shared with FinancialYear openingBalances. */
export const OPENING_BALANCE_FIELDS: Array<{ key: keyof BooksOpeningBalances; label: string; hint: string }> = [
  { key: 'cash', label: 'Cash in hand', hint: 'Physical cash as of cutover' },
  { key: 'bank', label: 'Bank balance', hint: 'Company bank(s) total' },
  { key: 'outstanding', label: 'Loans receivable', hint: 'Sum of active loan outstandings' },
  { key: 'capital', label: 'Capital / equity', hint: 'Owner capital & reserves' },
  { key: 'loansTaken', label: 'Loans taken', hint: 'Borrowings / OD payable' },
  { key: 'creditors', label: 'Creditors', hint: 'Payables to vendors' },
  { key: 'inventory', label: 'Inventory', hint: 'Consultancy stock value if any' },
  { key: 'employeeAdvances', label: 'Employee advances', hint: 'Advances outstanding' },
  { key: 'ytdInterestIncome', label: 'YTD interest income', hint: 'Optional mid-year P&L seed' },
  { key: 'ytdExpenses', label: 'YTD expenses', hint: 'Optional mid-year P&L seed' },
];

/** Schedule start so first installment falls on nextDueDate (engine uses start + 1 month). */
export function scheduleStartFromNextDue(nextDueYmd: string): string {
  const d = new Date(`${nextDueYmd}T12:00:00`);
  if (!Number.isFinite(d.getTime())) return nextDueYmd;
  d.setMonth(d.getMonth() - 1);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
